import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Clock } from 'lucide-react'

export interface FulfillmentPoint {
  week: string
  median_days: number
  avg_days: number
  shipped_count: number
}

const RECENT_WEEKS = 4

// Average over the last few weeks, weighted by how many orders went out each week.
function recentSummary(data: FulfillmentPoint[]): { avg: number; count: number } | null {
  const recent = data.slice(-RECENT_WEEKS)
  const count = recent.reduce((sum, p) => sum + p.shipped_count, 0)
  if (count === 0) return null
  const total = recent.reduce((sum, p) => sum + p.avg_days * p.shipped_count, 0)
  return { avg: total / count, count }
}

function formatWeek(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const MIN_POINTS_FOR_TREND = 3

export default function FulfillmentTimeChart({
  data,
  loading,
}: {
  data: FulfillmentPoint[]
  loading: boolean
}) {
  const hasEnoughData = data.length >= MIN_POINTS_FOR_TREND
  const recent = recentSummary(data)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Fulfillment time
        </CardTitle>
        <CardDescription>
          Days from order placed to picked and packed (or shipped, if sooner). Median of the orders that
          went out each week, last 6 months.
        </CardDescription>
        {!loading && recent && (
          <p className="text-sm pt-1">
            <span className="text-2xl font-semibold tabular-nums">{recent.avg.toFixed(1)}</span>{' '}
            <span className="text-muted-foreground">
              days on average over the last {RECENT_WEEKS} weeks ({recent.count} order{recent.count === 1 ? '' : 's'})
            </span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : !hasEnoughData ? (
          <p className="py-14 text-sm text-muted-foreground text-center px-4">
            Not enough orders have gone out yet to show a fulfillment-time trend. This fills in as orders
            are packed and shipped.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="week"
                tickFormatter={formatWeek}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                label={{
                  value: 'days',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' },
                }}
              />
              <Tooltip
                labelFormatter={(label: unknown) => `Week of ${formatWeek(String(label))}`}
                formatter={(value: unknown, _name: unknown, item: { payload?: FulfillmentPoint }): [string, string] => {
                  const p = item?.payload
                  const extra = p
                    ? ` (avg ${p.avg_days.toFixed(1)}, ${p.shipped_count} order${p.shipped_count === 1 ? '' : 's'})`
                    : ''
                  return [`${Number(value).toFixed(1)} days${extra}`, 'Median']
                }}
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--popover-foreground))',
                }}
              />
              <Line
                type="monotone"
                dataKey="median_days"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
